import { inject, Injectable, InjectionToken, Provider, signal } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { lastValueFrom } from 'rxjs';
import { LOGGER } from '../../shared/logging/log.service';
import { LvrErrorData, LvrErrorDialog } from '../components/error-dialog/error-dialog';
import { ENVIRONMENT } from '../../../environments/environment.interface';

export const ERROR_PROVIDER = new InjectionToken<ErrorProvider>('ERROR_PROVIDER');

export interface ErrorProvider {
	handleError(error: unknown, showDialog?: boolean): Promise<void>;
}

@Injectable()
export class LvrErrorService implements ErrorProvider {
	private readonly log = inject(LOGGER);
	private readonly dialog = inject(MatDialog);
	private readonly env = inject(ENVIRONMENT);

	private readonly currentDialog = signal<MatDialogRef<LvrErrorDialog> | undefined>(undefined);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public async handleError(error: any, showDialog?: boolean): Promise<void> {
		const msg = typeof error === 'string' ? error : (error.message ?? error.error.message ?? 'Fehler ist aufgetreten.');
		const err: LvrErrorData = {
			message: msg,
			title: error.name ?? 'Unbekannter Fehler',
			stackTrace: error.stack ?? JSON.stringify(error),
			currentUrl: window.location.href,
		};

		this.log.error({
			message: err.message,
			name: err.title,
			stack: err.stackTrace,
		});

		if (!this.env.showDevTools || !showDialog || this.currentDialog()) return;

		// Special case for ExpressionChanged errors in Angular
		if (err.message.includes('ExpressionChangedAfterItHasBeenCheckedError')) return;

		const dialogRef = this.dialog.open(LvrErrorDialog, {
			data: err,
			minWidth: '400px',
		});
		this.currentDialog.set(dialogRef);

		await lastValueFrom(dialogRef.afterClosed());

		this.currentDialog.set(undefined);
	}
}

export const provideErrorService: Provider = {
	provide: ERROR_PROVIDER,
	useClass: LvrErrorService,
};
